/**
 * Page setup in exports: header and footer bands, margins and orientation for the Word export
 * (docx section properties, Header / Footer parts) and the print window / HTML export (@page
 * size and margins, CSS page margin boxes).
 *
 * page_setup is data, never HTML (see src/utils/pageSetup.js). Everything here starts from
 * `normalizePageSetup`, escapes slot text for its target (CSS strings through
 * `slotTextToCssContent`, HTML through `escapeHtml`, Word through text runs) and checks image
 * URLs with `isAllowedImageUrl` again right before using them, so no data:, blob: or third-party
 * URL stored in a page setup is ever fetched or rendered.
 *
 * Every renderer lays a band out like the Pages view (src/utils/pageSetup.js band layout): a
 * band image is drawn at `bandImageBox` (its height, but never taller than the margin less
 * BAND_EDGE_INSET_PX at the paper edge and BAND_INSET_PX at the body, nor wider than its slot),
 * and slot text that cannot be clipped by
 * the renderer (Word paragraphs, Chrome's margin boxes) is cut to its slot's width with an
 * ellipsis (`fitSlotParts`), like the Pages view's one line with text-overflow: ellipsis.
 *
 * Word: three slots per band on one paragraph with a centre tab stop (half the content width)
 * and a right tab stop (the right margin); {page} / {pages} are real PAGE / NUMPAGES fields;
 * images are inline with the image's own aspect ratio; the header / footer distance shrinks so
 * the tallest band image is centred in the margin (as in the Pages view) and never pushes the
 * body down; a different first page sets `titlePage` with its own `first` header and footer
 * (empty when none).
 *
 * PDF (print window): `@page { size; margin }` plus margin boxes (@top-left, @top-center,
 * @top-right, @bottom-*) with `content:` strings and counter(page) / counter(pages), and
 * `@page :first` for the first-page bands. Band images print in margin boxes as
 * `content: image-set(url(...) Nx)`, where N scales the image to its band height (tested in
 * Chrome's print-to-PDF: `content: url()` alone ignores the box size and prints at the image's
 * natural size, and position: fixed fallbacks land in the wrong place). The images are embedded
 * as data URLs when they can be fetched, so printing does not wait on the network.
 */

import {
  AlignmentType,
  Footer,
  Header,
  ImageRun,
  PageNumber,
  Paragraph,
  Tab,
  TabStopType,
  TextRun,
} from 'docx';
import {
  BAND_EDGE_INSET_PX,
  BAND_INSET_PX,
  SLOT_POSITIONS,
  bandImageBox,
  bandSlotWidth,
  bandSpansCenter,
  bandsForPage,
  escapeHtml,
  fitSlotParts,
  isAllowedImageUrl,
  isBandEmpty,
  normalizePageSetup,
  slotTextToCssContent,
  tokenizeSlotText,
} from '../utils/pageSetup';

/** Band text size in Word (half-points): 8.5pt, about the Pages view's 11px. */
export const WORD_BAND_SIZE_HALF_POINTS = 17;
/** Band text color (the Pages view's muted page number color). */
export const BAND_TEXT_COLOR = '716C65';
/** Band font (matches the export body font). */
const BAND_FONT = 'Arial';
/** Band text size in print (px). */
export const PRINT_BAND_FONT_PX = 11;
/** Band text size in Word in px (8.5 pt), for fitting text to a slot. */
const WORD_BAND_FONT_PX = (WORD_BAND_SIZE_HALF_POINTS / 2) * (96 / 72);
/** Twips per CSS px (1440 per inch, 96 px per inch). */
const TWIPS_PER_PX = 15;

/** CSS margin box names per band and slot. */
export const MARGIN_BOXES = Object.freeze({
  header: Object.freeze({ left: 'top-left', center: 'top-center', right: 'top-right' }),
  footer: Object.freeze({ left: 'bottom-left', center: 'bottom-center', right: 'bottom-right' }),
});

/** Image types Word renders (docx media). */
const WORD_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif']);

/**
 * Read an image's pixel size from its header bytes (PNG, JPEG, GIF, WebP).
 * @param {Uint8Array|ArrayBuffer|null|undefined} input - Image bytes.
 * @returns {{width: number, height: number, mimeType: string}|null} Size and type, or null when unknown.
 */
export function readImageSize(input) {
  if (!input) return null;
  const b = input instanceof Uint8Array ? input : new Uint8Array(input);
  const be16 = (i) => (b[i] << 8) | b[i + 1];
  const le16 = (i) => b[i] | (b[i + 1] << 8);
  const be32 = (i) => ((b[i] << 24) >>> 0) + (b[i + 1] << 16) + (b[i + 2] << 8) + b[i + 3];
  const le24 = (i) => b[i] | (b[i + 1] << 8) | (b[i + 2] << 16);
  const ascii = (i, n) => String.fromCharCode(...b.subarray(i, i + n));
  const ok = (width, height, mimeType) => (width > 0 && height > 0 ? { width, height, mimeType } : null);

  if (b.length >= 24 && b[0] === 0x89 && ascii(1, 3) === 'PNG') {
    return ok(be32(16), be32(20), 'image/png');
  }
  if (b.length >= 10 && ascii(0, 4) === 'GIF8') {
    return ok(le16(6), le16(8), 'image/gif');
  }
  if (b.length >= 4 && b[0] === 0xff && b[1] === 0xd8) {
    let i = 2;
    while (i + 9 < b.length) {
      if (b[i] !== 0xff) {
        i += 1;
        continue;
      }
      const marker = b[i + 1];
      if (marker === 0xff) {
        i += 1;
        continue;
      }
      if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
        i += 2;
        continue;
      }
      const length = be16(i + 2);
      const isFrame = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
      if (isFrame) return ok(be16(i + 7), be16(i + 5), 'image/jpeg');
      if (length < 2) return null;
      i += 2 + length;
    }
    return null;
  }
  if (b.length >= 30 && ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') {
    const chunk = ascii(12, 4);
    if (chunk === 'VP8 ') return ok(le16(26) & 0x3fff, le16(28) & 0x3fff, 'image/webp');
    if (chunk === 'VP8L') {
      const bits = b[21] | (b[22] << 8) | (b[23] << 16) | (b[24] << 24);
      return ok((bits & 0x3fff) + 1, ((bits >> 14) & 0x3fff) + 1, 'image/webp');
    }
    if (chunk === 'VP8X') return ok(le24(24) + 1, le24(27) + 1, 'image/webp');
  }
  return null;
}

/**
 * Base64 of bytes (chunked, safe for large images).
 * @param {Uint8Array} bytes - Bytes.
 * @returns {string} Base64.
 */
function bytesToBase64(bytes) {
  let binary = '';
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) {
    binary += String.fromCharCode(...bytes.subarray(i, i + step));
  }
  return btoa(binary);
}

/**
 * Every image URL a page setup's bands use (allowlisted only, each once).
 * @param {unknown} pageSetup - Page setup (raw or normalized).
 * @returns {string[]} Image URLs.
 */
export function collectBandImageSources(pageSetup) {
  const setup = normalizePageSetup(pageSetup);
  const bands = [setup.header, setup.footer];
  if (setup.differentFirstPage) bands.push(setup.firstHeader, setup.firstFooter);
  const sources = new Set();
  bands.forEach((band) => {
    if (!band) return;
    SLOT_POSITIONS.forEach((position) => {
      const slot = band[position];
      if (slot && slot.type === 'image' && isAllowedImageUrl(slot.src)) sources.add(slot.src);
    });
  });
  return Array.from(sources);
}

/**
 * Measure an image URL by loading it in an <img> (browser only; no CORS needed for the size).
 * @param {string} src - Image URL.
 * @param {number} [timeoutMs=4000] - Give up after this long.
 * @returns {Promise<{width: number, height: number}|null>} Natural size, or null.
 */
function measureImageUrl(src, timeoutMs = 4000) {
  if (typeof Image === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => resolve(null), timeoutMs);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img.naturalWidth > 0 && img.naturalHeight > 0 ? { width: img.naturalWidth, height: img.naturalHeight } : null);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = src;
  });
}

/**
 * Re-encode an image Word cannot show (WebP, SVG) as PNG through a canvas (browser only).
 * @param {Uint8Array} bytes - Image bytes.
 * @param {string} mimeType - Their type.
 * @returns {Promise<Uint8Array|null>} PNG bytes, or null when this environment cannot do it.
 */
async function toPngBytes(bytes, mimeType) {
  try {
    if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null;
    const bitmap = await createImageBitmap(new Blob([bytes], { type: mimeType }));
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(bitmap, 0, 0);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    return new Uint8Array(await blob.arrayBuffer());
  } catch {
    return null;
  }
}

/**
 * Load the header/footer images of a page setup for an export. Only allowlisted URLs are
 * fetched (credentials omitted). An image that cannot be fetched is still measured through an
 * <img> when possible, so the print window can show it from its URL; one that cannot be measured
 * is left out of the bands.
 *
 * @param {unknown} pageSetup - Page setup.
 * @param {object} [options] - Options.
 * @param {typeof fetch} [options.fetchImpl] - fetch (default: global fetch).
 * @param {(src: string) => Promise<{width: number, height: number}|null>} [options.measure] - Size fallback.
 * @returns {Promise<Map<string, {src: string, width: number, height: number, mimeType: string|null, bytes: Uint8Array|null, wordBytes: Uint8Array|null, dataUrl: string|null}>>}
 *   Images by URL (`wordBytes`: PNG, JPEG or GIF bytes Word can show).
 */
export async function loadBandImages(pageSetup, options = {}) {
  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);
  const measure = options.measure || measureImageUrl;
  const images = new Map();
  await Promise.all(collectBandImageSources(pageSetup).map(async (src) => {
    let bytes = null;
    let size = null;
    try {
      if (fetchImpl) {
        const response = await fetchImpl(src, { credentials: 'omit' });
        if (response && response.ok) {
          bytes = new Uint8Array(await response.arrayBuffer());
          size = readImageSize(bytes);
        }
      }
    } catch {
      bytes = null;
    }
    if (!size) {
      const measured = await measure(src).catch(() => null);
      if (measured) size = { ...measured, mimeType: null };
    }
    if (!size) return;
    const mimeType = size.mimeType || null;
    const known = Boolean(bytes && mimeType);
    let wordBytes = null;
    if (known) wordBytes = WORD_IMAGE_TYPES.has(mimeType) ? bytes : await toPngBytes(bytes, mimeType);
    images.set(src, {
      src,
      width: size.width,
      height: size.height,
      mimeType,
      bytes: known ? bytes : null,
      wordBytes,
      dataUrl: known ? `data:${mimeType};base64,${bytesToBase64(bytes)}` : null,
    });
  }));
  return images;
}

/**
 * Size a band image is drawn at (see `bandImageBox`). Without a geometry (older callers) it is
 * the stored height with the image's aspect ratio.
 * @param {{height: number}} slot - Image slot.
 * @param {{width: number, height: number}} image - Loaded image.
 * @param {{geometry?: object, span?: boolean}} [layout] - Page geometry and whether only the centre slot is used.
 * @returns {{width: number, height: number}} Size (px).
 */
const bandImageSize = (slot, image, layout = {}) => {
  if (layout.geometry) {
    const box = bandImageBox({ height: slot.height, naturalWidth: image.width, naturalHeight: image.height, geometry: layout.geometry, span: layout.span });
    return { width: box.width || Math.max(1, Math.round((box.height * image.width) / image.height)), height: box.height };
  }
  return { width: Math.max(1, Math.round((slot.height * image.width) / image.height)), height: slot.height };
};

/**
 * Width a slot's text may take (px; 0 = no limit without a geometry).
 * @param {{geometry?: object, span?: boolean}} layout - Layout.
 * @returns {number} Width.
 */
const slotTextWidth = (layout = {}) => (layout.geometry ? bandSlotWidth(layout.geometry, layout.span) : 0);

/**
 * Whether a slot's text uses {page} or {pages}.
 * @param {object} slot - Slot.
 * @returns {boolean} True for page number text.
 */
const usesPageFields = (slot) => slot.type === 'text' && /\{pages?\}/.test(slot.text);

/* ------------------------------------------------------------------------------------------ */
/* Print window and HTML export                                                                 */
/* ------------------------------------------------------------------------------------------ */

/**
 * CSS declarations for one margin box, or null when the slot shows nothing.
 * @param {object} slot - Normalized slot.
 * @param {object} ctx - Field context.
 * @param {Map} images - Loaded band images.
 * @param {{geometry?: object, span?: boolean}} [layout] - Page geometry and band span.
 * @returns {string|null} `content: ...;` declaration.
 */
function marginBoxContent(slot, ctx, images, layout = {}) {
  if (!slot) return null;
  if (slot.type === 'text') {
    if (!slot.text.trim()) return null;
    // Chrome ignores overflow / text-overflow in margin boxes: cut the text to its slot here.
    return `content: ${slotTextToCssContent(slot.text, ctx, { maxWidth: slotTextWidth(layout), fontPx: PRINT_BAND_FONT_PX })};`;
  }
  if (slot.type === 'image') {
    if (!isAllowedImageUrl(slot.src)) return null;
    const image = images && images.get(slot.src);
    if (!image || !(image.height > 0)) return null;
    const url = image.dataUrl || slot.src;
    // Never anything but an allowlisted https URL or the data URL built from its bytes.
    if (url !== slot.src && !/^data:image\/(png|jpeg|gif|webp);base64,[A-Za-z0-9+/=]+$/.test(url)) return null;
    const { height } = bandImageSize(slot, image, layout);
    const resolution = Math.round((image.height / height) * 10000) / 10000;
    return `content: image-set(url("${url}") ${resolution}x);`;
  }
  return null;
}

/**
 * Margin box rules for a header and a footer band.
 * @param {object|null} header - Header band.
 * @param {object|null} footer - Footer band.
 * @param {object} ctx - Field context.
 * @param {Map} images - Loaded band images.
 * @param {boolean} explicitNone - Write `content: none` for empty boxes (overrides in @page :first).
 * @param {object} [geometry] - Page geometry (fits text and images to their slots).
 * @returns {string} CSS.
 */
function marginBoxRules(header, footer, ctx, images, explicitNone, geometry) {
  const rules = [];
  [['header', header], ['footer', footer]].forEach(([kind, band]) => {
    const layout = { geometry, span: bandSpansCenter(band) };
    SLOT_POSITIONS.forEach((position) => {
      const content = marginBoxContent(band ? band[position] : null, ctx, images, layout);
      const box = MARGIN_BOXES[kind][position];
      if (content) {
        rules.push(`    @${box} { ${content} }`);
      } else if (explicitNone) {
        rules.push(`    @${box} { content: none; }`);
      }
    });
  });
  return rules.join('\n');
}

/**
 * The print CSS for a page setup: `@page` size, orientation and margins, the header and footer
 * bands as margin boxes, and `@page :first` for a different first page.
 *
 * @param {object} params - Inputs.
 * @param {object} params.geometry - `resolvePageGeometry(pageSetup, paperSize)`.
 * @param {unknown} params.pageSetup - Page setup.
 * @param {object} [params.fieldContext] - Live-field values (title, customer, project, date).
 * @param {Map} [params.images] - Loaded band images (`loadBandImages`).
 * @returns {string} CSS (one `@page` rule, plus `@page :first` when needed).
 */
export function buildPrintPageCss({ geometry, pageSetup, fieldContext = {}, images = new Map() }) {
  const setup = normalizePageSetup(pageSetup);
  // The Pages view's band padding: BAND_EDGE_INSET_PX at the paper edge (printers clip there),
  // BAND_INSET_PX at the body, the content centred in what is left (tested in Chrome's
  // print-to-PDF: margin boxes take padding, and @page :first keeps it).
  const boxStyle = (kind) => [
    `font-family: Arial, Helvetica, sans-serif`,
    `font-size: ${PRINT_BAND_FONT_PX}px`,
    `line-height: 1.2`,
    kind === 'header'
      ? `padding: ${BAND_EDGE_INSET_PX}px 0 ${BAND_INSET_PX}px`
      : `padding: ${BAND_INSET_PX}px 0 ${BAND_EDGE_INSET_PX}px`,
    `color: #${BAND_TEXT_COLOR}`,
    `white-space: nowrap`,
    `overflow: hidden`,
    `text-overflow: ellipsis`,
  ].join('; ');
  const boxDefaults = ['header', 'footer']
    .flatMap((kind) => Object.values(MARGIN_BOXES[kind]).map((box) => `    @${box} { ${boxStyle(kind)}; }`))
    .join('\n');
  const main = marginBoxRules(setup.header, setup.footer, fieldContext, images, false, geometry);
  let css = `@page {
    size: ${geometry.cssSize};
    margin: ${geometry.cssMargin};
${boxDefaults}
${main}
}`;
  if (setup.differentFirstPage) {
    const first = bandsForPage(setup, 1);
    css += `
@page :first {
${marginBoxRules(first.header, first.footer, fieldContext, images, true, geometry)}
}`;
  }
  return css;
}

/**
 * Screen-only header or footer block for the HTML export (and the print window preview). Page
 * numbers have no meaning in one continuous page, so slots with {page} / {pages} are left out;
 * in print the margin boxes show the bands instead (this block is hidden there).
 *
 * @param {object|null} band - Band.
 * @param {'header'|'footer'} kind - Which band.
 * @param {object} ctx - Field context.
 * @param {Map} images - Loaded band images.
 * @param {object} [geometry] - Page geometry (images sized like the Pages view).
 * @returns {string} HTML ('' when the band shows nothing).
 */
export function buildBandHtml(band, kind, ctx = {}, images = new Map(), geometry = null) {
  if (!band || isBandEmpty(band)) return '';
  const layout = { geometry, span: bandSpansCenter(band) };
  let any = false;
  const slots = SLOT_POSITIONS.map((position) => {
    const slot = band[position];
    let inner = '';
    if (slot && slot.type === 'text' && slot.text.trim() && !usesPageFields(slot)) {
      inner = escapeHtml(tokenizeSlotText(slot.text, ctx).map((part) => part.text).join(''));
    } else if (slot && slot.type === 'image' && isAllowedImageUrl(slot.src)) {
      const image = images.get(slot.src);
      const url = image && image.dataUrl ? image.dataUrl : slot.src;
      const size = image ? bandImageSize(slot, image, layout) : { width: null, height: slot.height };
      const width = size.width ? ` width="${size.width}"` : '';
      inner = `<img src="${escapeHtml(url)}" alt="${escapeHtml(slot.alt)}" height="${size.height}"${width} style="height:${size.height}px;width:auto;max-width:100%">`;
    }
    if (inner) any = true;
    return `<div class="document-band__slot document-band__slot--${position}">${inner}</div>`;
  });
  if (!any) return '';
  const tag = kind === 'header' ? 'header' : 'footer';
  return `<${tag} class="document-band document-band--${kind}">${slots.join('')}</${tag}>`;
}

/** CSS for the screen header/footer blocks and wrapped (floated) images in exported HTML. */
export const BAND_AND_WRAP_EXPORT_CSS = `
        .document-band {
            display: grid;
            grid-template-columns: 1fr auto 1fr;
            align-items: center;
            gap: 12px;
            font-family: Arial, Helvetica, sans-serif;
            font-size: ${PRINT_BAND_FONT_PX}px;
            line-height: 1.2;
            color: #${BAND_TEXT_COLOR};
        }
        .document-band--header { margin-bottom: 24px; }
        .document-band--footer { margin-top: 24px; }
        .document-band__slot {
            min-width: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .document-band__slot--center { text-align: center; }
        .document-band__slot--right { text-align: right; }
        .document-band img {
            display: inline-block;
            max-width: none;
            margin: 0;
            border-radius: 0;
            box-shadow: none;
            vertical-align: middle;
        }
        /* Wrapped images (data-wrap) float with the text beside them, as in the editor */
        .document-content { display: flow-root; }
        img[data-wrap="left"] {
            float: left;
            max-width: 60%;
            margin: 0.25em 0.75em 0.75em 0;
        }
        img[data-wrap="right"] {
            float: right;
            max-width: 60%;
            margin: 0.25em 0 0.75em 0.75em;
        }
        .page-break,
        div[data-page-break] { clear: both; }
        /* A wrapped image and the block after it print on the same page (print window only) */
        .q-wrap-group {
            break-inside: avoid;
            page-break-inside: avoid;
        }
        @media print {
            .document-band { display: none !important; }
        }
`;

/* ------------------------------------------------------------------------------------------ */
/* Word                                                                                         */
/* ------------------------------------------------------------------------------------------ */

/**
 * Text runs for slot text: literal text, and PAGE / NUMPAGES fields for {page} / {pages}. Cut to
 * `maxWidth` with an ellipsis, so a long slot never wraps the band onto a second line or pushes
 * the next slot past its tab stop.
 * @param {string} text - Slot text.
 * @param {object} ctx - Field context.
 * @param {number} [maxWidth] - Slot width (px; 0 = no limit).
 * @returns {TextRun[]} Runs.
 */
function wordTextRuns(text, ctx, maxWidth = 0) {
  const style = { font: BAND_FONT, size: WORD_BAND_SIZE_HALF_POINTS, color: BAND_TEXT_COLOR };
  const parts = fitSlotParts(tokenizeSlotText(text, ctx, { keepPageFields: true }), maxWidth, WORD_BAND_FONT_PX);
  return parts.map((part) => {
    if (part.type === 'page') return new TextRun({ ...style, children: [PageNumber.CURRENT] });
    if (part.type === 'pages') return new TextRun({ ...style, children: [PageNumber.TOTAL_PAGES] });
    return new TextRun({ ...style, text: part.text });
  });
}

/**
 * Size a band image slot is drawn at in Word, or null when it does not render.
 * @param {object} slot - Normalized slot.
 * @param {Map} images - Loaded band images.
 * @param {{geometry?: object, span?: boolean}} layout - Layout.
 * @returns {{width: number, height: number}|null} Size (px).
 */
function wordImageSize(slot, images, layout) {
  if (!slot || slot.type !== 'image' || !isAllowedImageUrl(slot.src)) return null;
  const image = images && images.get(slot.src);
  if (!image || !image.wordBytes) return null;
  return bandImageSize(slot, image, layout);
}

/**
 * Runs for one slot ([] when it shows nothing).
 * @param {object} slot - Normalized slot.
 * @param {object} ctx - Field context.
 * @param {Map} images - Loaded band images.
 * @param {{geometry?: object, span?: boolean}} [layout] - Page geometry and band span.
 * @returns {Array} Runs.
 */
function wordSlotRuns(slot, ctx, images, layout = {}) {
  if (!slot) return [];
  if (slot.type === 'text') return slot.text.trim() ? wordTextRuns(slot.text, ctx, slotTextWidth(layout)) : [];
  const size = wordImageSize(slot, images, layout);
  if (size) {
    const image = images.get(slot.src);
    return [new ImageRun({
      data: image.wordBytes,
      transformation: { width: size.width, height: size.height },
      altText: { name: 'Header image', description: slot.alt || '', title: slot.alt || '' },
    })];
  }
  return [];
}

/**
 * Tallest band image Word draws in some bands (px; 0 when none).
 * @param {Array<object|null>} bands - Bands.
 * @param {Map} images - Loaded band images.
 * @param {object} geometry - Page geometry.
 * @returns {number} Height.
 */
function tallestWordImage(bands, images, geometry) {
  let tallest = 0;
  bands.forEach((band) => {
    if (!band) return;
    const layout = { geometry, span: bandSpansCenter(band) };
    SLOT_POSITIONS.forEach((position) => {
      const size = wordImageSize(band[position], images, layout);
      if (size && size.height > tallest) tallest = size.height;
    });
  });
  return tallest;
}

/**
 * Header or footer distance (twips) that centres the tallest band image in the margin less
 * BAND_EDGE_INSET_PX at the paper edge and BAND_INSET_PX at the body, as the Pages view does (so
 * it is never closer than BAND_EDGE_INSET_PX to the edge); never more than the default (half
 * the margin), so text-only bands keep it.
 * @param {number} tallestPx - Tallest image (px).
 * @param {number} marginTwips - Page margin (twips).
 * @param {number} defaultTwips - Default distance (twips).
 * @returns {number} Distance (twips).
 */
function bandDistanceTwips(tallestPx, marginTwips, defaultTwips) {
  if (!(tallestPx > 0)) return defaultTwips;
  const edge = BAND_EDGE_INSET_PX * TWIPS_PER_PX;
  const room = marginTwips - edge - BAND_INSET_PX * TWIPS_PER_PX;
  return Math.max(0, Math.min(defaultTwips, Math.round(edge + (room - tallestPx * TWIPS_PER_PX) / 2)));
}

/**
 * One band as a Word paragraph: left slot, then a tab to the centre stop, the centre slot, a tab
 * to the right stop and the right slot.
 * @param {object|null} band - Band.
 * @param {object} ctx - Field context.
 * @param {Map} images - Loaded band images.
 * @param {number} contentWidthTwips - Width between the margins (twips).
 * @param {object} [geometry] - Page geometry: fits text and images to their slots.
 * @returns {Paragraph|null} Paragraph, or null when the band shows nothing.
 */
export function buildWordBandParagraph(band, ctx, images, contentWidthTwips, geometry = null) {
  if (!band) return null;
  const layout = { geometry, span: bandSpansCenter(band) };
  const [left, center, right] = SLOT_POSITIONS.map((position) => wordSlotRuns(band[position], ctx, images, layout));
  if (!left.length && !center.length && !right.length) return null;
  const tab = () => new TextRun({ font: BAND_FONT, size: WORD_BAND_SIZE_HALF_POINTS, children: [new Tab()] });
  const children = [...left];
  if (center.length || right.length) children.push(tab(), ...center);
  if (right.length) children.push(tab(), ...right);
  return new Paragraph({
    children,
    alignment: AlignmentType.LEFT,
    spacing: { before: 0, after: 0 },
    tabStops: [
      { type: TabStopType.CENTER, position: Math.round(contentWidthTwips / 2) },
      { type: TabStopType.RIGHT, position: contentWidthTwips },
    ],
  });
}

/**
 * Word headers and footers for a page setup.
 *
 * @param {object} params - Inputs.
 * @param {unknown} params.pageSetup - Page setup.
 * @param {object} params.geometry - `resolvePageGeometry(pageSetup, paperSize)`.
 * @param {object} [params.fieldContext] - Live-field values.
 * @param {Map} [params.images] - Loaded band images.
 * @returns {{headers: object|undefined, footers: object|undefined, titlePage: boolean, distance: {header: number, footer: number}}}
 *   docx section `headers` / `footers` (`default`, and `first` with a different first page), and
 *   the header / footer distances (twips) for the section's page margin.
 */
export function buildWordHeadersFooters({ pageSetup, geometry, fieldContext = {}, images = new Map() }) {
  const setup = normalizePageSetup(pageSetup);
  const contentWidthTwips = geometry.twips.width - geometry.twips.margin.left - geometry.twips.margin.right;
  const part = (Kind, band) => {
    const paragraph = buildWordBandParagraph(band, fieldContext, images, contentWidthTwips, geometry);
    return paragraph ? new Kind({ children: [paragraph] }) : null;
  };
  const headers = {};
  const footers = {};
  const header = part(Header, setup.header);
  const footer = part(Footer, setup.footer);
  if (header) headers.default = header;
  if (footer) footers.default = footer;
  if (setup.differentFirstPage) {
    const first = bandsForPage(setup, 1);
    // An empty first-page part, so page 1 never falls back to the default band.
    headers.first = part(Header, first.header) || new Header({ children: [new Paragraph({ children: [] })] });
    footers.first = part(Footer, first.footer) || new Footer({ children: [new Paragraph({ children: [] })] });
  }
  const first = setup.differentFirstPage ? bandsForPage(setup, 1) : null;
  const { margin } = geometry.twips;
  const distance = {
    header: bandDistanceTwips(tallestWordImage([setup.header, first && first.header], images, geometry), margin.top, margin.header),
    footer: bandDistanceTwips(tallestWordImage([setup.footer, first && first.footer], images, geometry), margin.bottom, margin.footer),
  };
  return {
    headers: Object.keys(headers).length ? headers : undefined,
    footers: Object.keys(footers).length ? footers : undefined,
    titlePage: setup.differentFirstPage,
    distance,
  };
}
